import React, { FunctionComponent, useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useRouter } from "next/router";

import {
  GetUserOrOrgQuery,
  Org,
  Project,
  User,
} from "src/lib/graphql/types";

import {
  getUserOrOrgQuery,
} from "src/lib/graphql/user.queries";
import PageNumbers from "../../PageNumbers";
import { useUser } from "src/hooks/useUser";
import { ProfileDataResolver } from "./ProfileDataResolver";
import ProfileTabs from "./ProfileTabs";

export type ProfileProjectSummary = Pick<
  Project,
  | "id"
  | "name"
  | "description"
  | "type"
  | "createdAt"
  | "updatedAt"
  | "pathWithNamespace"
  | "popularity"
  | "avatar"
  | "forkCount"
  | "starCount"
  | "dependents"
  | "visibility"
> & {
  owner?:
    | Pick<Org, "avatar" | "image">
    | Pick<User, "image">;
};

type ProfileListingCardsProps = {
  entity: Org | User;
};

export type TabOptionKey =
  | "All"
  | "Simulation"
  | "Dataset"
  | "Behavior"
  | "Members"

export type TabOption = {
  value: TabOptionKey;
  alias: string;
};

export const profilePagePathsMap: Record<string, TabOptionKey> = {
  models: "Simulation",
  simulations: "Simulation",
  behaviors: "Behavior",
  members: "Members",
  people: "Members",
  projects: "All",
  data: "Dataset",
};

const ProfileData: FunctionComponent<ProfileListingCardsProps> = ({
  entity,
}) => {
  const router = useRouter();

  const { projectSlug, shortname } = router.query;

  const [user] = useUser();

  const userIsMember = user?.memberOf?.find(
    (info) => info.org?.shortname === entity.shortname
  );

  function getInitialTabOption(): TabOption {
    if (projectSlug?.[0] && profilePagePathsMap[projectSlug?.[0]]) {
      return {
        alias: profilePagePathsMap[projectSlug?.[0]],
        value: profilePagePathsMap[projectSlug?.[0]],
      };
    }
    return { alias: "All", value: "All" };
  }

  const initialTabOption: TabOption = getInitialTabOption();

  const [tab, setTab] = useState<TabOption>(initialTabOption);
  const [sort, setSort] = useState("updatedAt");

  useEffect(() => {
    if (
      projectSlug?.[0] &&
      profilePagePathsMap[projectSlug?.[0]] === "Members" &&
      !userIsMember
    ) {
      router.push(`/@${shortname}/projects`);
      setTab({ alias: "All", value: "All" });
    }
  }, [userIsMember, shortname, projectSlug?.[0]]);

  const baseVariables: any = {
    shortname: entity.shortname,
    page: 1,
    sort: "updatedAt",
    withProjects: true
  };
  baseVariables.types = ["All", "Members"].includes(tab.value)
    ? undefined
    : tab.value;

  // We use different queries to fetch an entity's owned projects...
  const [getProjects, userOrOrgProjects] = useLazyQuery<GetUserOrOrgQuery>(
    getUserOrOrgQuery,
    {
      errorPolicy: "all",
      fetchPolicy: "cache-first",
      notifyOnNetworkStatusChange: true,
      variables: baseVariables,
    }
  );

  const projectResults = userOrOrgProjects.data?.userOrOrg?.projects;
  const { error } = userOrOrgProjects;
  const { results, page, totalCount } = projectResults || {};

  const getLoading = () => {
    if (tab.value === "Members") {
      return false;
    }
    return userOrOrgProjects.loading;
  };

  const loading = getLoading();

  useEffect(() => {
    getProjects({
      variables: {
        ...baseVariables,
        types: ["All", "Members"].includes(tab.value)
          ? undefined
          : tab.value,
        sort,
      },
    });
  }, [tab, sort]);

  const setPage = (newPage: number) => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    getProjects({
      variables: {
        ...baseVariables,
        page: newPage,
      },
    });
  };

  const Paginator = (
    <div className="paginator">
      <PageNumbers
        page={page || 1}
        perPage={10}
        setPage={setPage}
        totalRecords={totalCount || 0}
      />
    </div>
  );

  return (
    <div className="profile-listing-cards">
      <ProfileTabs
        setSort={setSort}
        setTab={setTab}
        tab={tab}
        sort={sort}
        userIsMember={userIsMember}
        entity={entity}
      />

      <ProfileDataResolver
        entity={entity}
        loading={loading}
        results={results}
        tab={tab}
        error={error}
      />

      {!loading && !!totalCount && totalCount > 10 && Paginator}
    </div>
  );
};

export default ProfileData;
